import type { ManyOfManyPoll, PollResponse } from '@tunasong/schemas'
import type { SlotTally } from './availability.js'
import { getSlotTally, rankDateSlots } from './availability.js'
import { getLiveSlotIndices } from './date-slot.js'

/**
 * A slot's tally with the silent members counted in.
 *
 * The open poll can leave the rest of the band implicit — `Waiting on Ada and Bo` sits right
 * below the rows and disambiguates. The closed page has no waiting-on line, and it shows an
 * avatar for yes and nothing at all for maybe or no, so an absence there is unreadable unless
 * the numbers themselves add up to everyone. `yes + maybe + no + noReply === total`, always.
 */
export interface SlotAttendance extends SlotTally {
  noReply: number
  total: number
}

/**
 * `memberCount` is ACL-derived and can lag or come back empty — a query result, not a fact
 * about the poll. Taking it as the denominator unchecked prints `2 of 0 available` and a
 * negative no-reply count, so the number of people who actually answered is the floor.
 */
export const getSlotAttendance = (responses: PollResponse[], idx: number, memberCount: number): SlotAttendance => {
  const tally = getSlotTally(responses, idx)
  const answered = tally.yes + tally.maybe + tally.no
  const total = Math.max(memberCount, answered)

  return { ...tally, noReply: total - answered, total }
}

/** Via `getLiveSlotIndices`, so a slot tombstoned after it was picked cannot still win. */
export const getWinnerIndices = (poll: ManyOfManyPoll) =>
  getLiveSlotIndices(poll).filter(idx => Boolean(poll.finalOption?.[idx]))

/**
 * Ranked, never chronological: the also-rans exist to make the decision legible — "this night
 * won with four, the next had two" — and that argument only reads in order of support.
 */
export const getAlsoRanIndices = (poll: ManyOfManyPoll, responses: PollResponse[]) =>
  rankDateSlots(poll, responses).filter(idx => !poll.finalOption?.[idx])

/**
 * The one sentence the page exists to say.
 *
 * One night names the date in the heading. Several put the count there and the dates on their
 * own lines below, because a heading that lists three timestamps is no longer a heading.
 *
 * Zero is reachable on a poll closed before the date-poll UI existed, and the page must not
 * assert a rehearsal that was never scheduled.
 */
export const formatWinnersLead = (labels: string[]) => {
  if (labels.length === 0) {
    return 'No date was picked'
  }
  return labels.length === 1 ? `Rehearsal set for ${labels[0]}` : `${labels.length} rehearsals set`
}

/**
 * An owner may close on a night nobody can make — nothing prevents it, and the page must not
 * imply otherwise. `0 of 5 available` is true and reads as a rendering failure, so the empty
 * case gets its own sentence instead.
 *
 * Past tense, and deliberately no `yet`. Closing is terminal: there is no future in which
 * somebody still might say yes, and `yet` promises one this poll can no longer deliver.
 */
export const formatAvailability = (attendance: SlotAttendance) =>
  attendance.yes === 0 ? 'Nobody said yes to this date.' : `${attendance.yes} of ${attendance.total} available`

/** Everyone the yes-only avatars cannot show. Undefined when the tally line already says it all. */
export const formatAttendanceDetail = (attendance: SlotAttendance) => {
  const parts = [
    attendance.maybe > 0 ? `${attendance.maybe} maybe` : undefined,
    attendance.no > 0 ? `${attendance.no} no` : undefined,
    attendance.noReply > 0 ? `${attendance.noReply} no reply` : undefined,
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : undefined
}

/**
 * Picking fewer nights than the band needed is allowed — the finalize step confirms it — so
 * the closed page has to carry the consequence. Over-picking is equally legitimate and needs
 * no notice: more rehearsals than you asked for is not a shortfall.
 */
export const formatShortfall = (picked: number, needed: number) =>
  picked < needed ? `${picked} of the ${needed} dates you needed` : undefined
